export const MULTIPLE_EXISTING_SYSTEMS_ENABLED = ['dev', 'test'].includes(
  process.env.VUE_APP_STAGE
);

export const EXISTING_SYSTEM_GROUPS = [
  {
    stateKey: 'pvSystems',
    payloadKey: 'pv_systems',
    powerKey: 'pv_system_W',
    dateKey: 'pv_system_implementing_date',
    selectedProp: 'hasPvSystem',
    powerProp: 'pvSystemWatt',
    dateProp: 'pvSystemDate',
    powerField: 'power_W',
  },
  {
    stateKey: 'pluginPvSystems',
    payloadKey: 'plugin_pv_systems',
    powerKey: 'plugin_pv_system_W',
    dateKey: 'plugin_pv_system_implementing_date',
    selectedProp: 'hasPluginPvSystem',
    powerProp: 'pluginPvSystemWatt',
    dateProp: 'pluginPvSystemDate',
    powerField: 'power_W',
  },
  {
    stateKey: 'cogenerationSystems',
    payloadKey: 'cogeneration_systems',
    powerKey: 'cogeneration_VA',
    dateKey: 'cogeneration_implementing_date',
    selectedProp: 'hasCogeneration',
    powerProp: 'cogenerationVA',
    dateProp: 'cogenerationDate',
    powerField: 'power_VA',
  },
  {
    stateKey: 'windPowerSystems',
    payloadKey: 'wind_power_systems',
    powerKey: 'wind_power_VA',
    dateKey: 'wind_power_implementing_date',
    selectedProp: 'hasWindPower',
    powerProp: 'windPowerVA',
    dateProp: 'windPowerDate',
    powerField: 'power_VA',
  },
  {
    stateKey: 'standbySystems',
    payloadKey: 'standby_systems',
    powerKey: 'standby_system_VA',
    dateKey: 'standby_system_implementing_date',
    selectedProp: 'hasStandbySystem',
    powerProp: 'standbySystemVA',
    dateProp: 'standbySystemDate',
    powerField: 'power_VA',
  },
  {
    stateKey: 'otherSystems',
    payloadKey: 'other_systems',
    powerKey: 'other_system_VA',
    dateKey: 'other_system_implementing_date',
    selectedProp: 'hasOtherSystem',
    powerProp: 'otherSystemVA',
    dateProp: 'otherSystemDate',
    powerField: 'power_VA',
  },
];

export const hasBlockedExistingSystems = (source) =>
  !MULTIPLE_EXISTING_SYSTEMS_ENABLED &&
  EXISTING_SYSTEM_GROUPS.some(({ stateKey, payloadKey }) =>
    [source?.[stateKey], source?.[payloadKey]].some(
      (systems) => Array.isArray(systems) && systems.length > 1
    )
  );

export const SINGLE_SYSTEM_WARNING =
  'Dieser Vorgang enthält mehrere Bestandsanlagen derselben Art. Eine Bearbeitung ist hier derzeit nicht möglich. Ihre Angaben bleiben erhalten. Bitte wenden Sie sich an den Support.';

export function legacyExistingSystemsProps(source) {
  return EXISTING_SYSTEM_GROUPS.reduce(
    (props, group) => {
      const systems = source[group.stateKey] || [];
      const first = systems[0] || {};
      props[group.selectedProp] = systems.length > 0;
      props[group.powerProp] = first[group.powerField] ?? null;
      props[group.dateProp] = first.implementing_date ?? null;
      return props;
    },
    { otherSystemName: source.otherSystems?.[0]?.name || '' }
  );
}

export function legacyExistingSystemsHandlers(source, emit) {
  const handlers = {};
  const event = (prop) => 'set' + prop.charAt(0).toUpperCase() + prop.slice(1);
  EXISTING_SYSTEM_GROUPS.forEach((group) => {
    let systems = source[group.stateKey] || [];
    const setSystems = (nextSystems) => {
      systems = nextSystems;
      emit(event(group.stateKey), systems);
    };
    const update = (field, value) => {
      const first = systems[0];
      // The legacy component clears value fields after unchecking a type.
      if (first) setSystems([{ ...first, [field]: value }]);
    };
    handlers[event(group.selectedProp)] = (selected) =>
      setSystems(selected ? [{ [group.powerField]: null }] : []);
    handlers[event(group.powerProp)] = (value) =>
      update(group.powerField, value);
    handlers[event(group.dateProp)] = (value) =>
      update('implementing_date', value);
    if (group.stateKey === 'otherSystems') {
      handlers.setOtherSystemName = (value) => update('name', value);
    }
  });
  return handlers;
}

// Convert only a verified single-entry payload; never silently discard extra entries.
export function existingSystemsPayloadForStage(payload) {
  if (MULTIPLE_EXISTING_SYSTEMS_ENABLED || payload == null) return payload;
  if (hasBlockedExistingSystems(payload))
    throw new Error(SINGLE_SYSTEM_WARNING);
  const result = { ...payload };
  EXISTING_SYSTEM_GROUPS.forEach((group) => {
    if (!Array.isArray(payload[group.payloadKey])) return;
    delete result[group.powerKey];
    delete result[group.dateKey];
    if (group.stateKey === 'otherSystems') delete result.other_system_name;
    const first = payload[group.payloadKey][0];
    if (first) {
      result[group.powerKey] = first[group.powerField];
      if (first.implementing_date)
        result[group.dateKey] = first.implementing_date;
      if (group.stateKey === 'otherSystems')
        result.other_system_name = first.name;
    }
    delete result[group.payloadKey];
  });
  assertSingleExistingSystemsPayload(result);
  return result;
}

export function assertSingleExistingSystemsPayload(payload) {
  if (MULTIPLE_EXISTING_SYSTEMS_ENABLED || payload == null) return;
  const powerKeys = EXISTING_SYSTEM_GROUPS.map((group) => group.powerKey);
  const allowed = new Set([
    ...powerKeys,
    ...EXISTING_SYSTEM_GROUPS.map((group) => group.dateKey),
    'other_system_name',
    'file_name',
  ]);
  if (
    typeof payload !== 'object' ||
    Array.isArray(payload) ||
    Object.entries(payload).some(
      ([key, value]) =>
        !allowed.has(key) ||
        (value != null &&
          (powerKeys.includes(key)
            ? !Number.isInteger(value) || value <= 0 || value > 2147483647
            : typeof value !== 'string'))
    )
  ) {
    throw new Error(
      'Production existing systems must use legacy scalar fields.'
    );
  }
}
